import { DEFAULT_CONFS, DEFAULT_DIVS } from "../../common/constants.ts";
import type { Conditions } from "../../common/types.ts";
import { unwrapGameAttribute } from "../../common/unwrapGameAttribute.ts";
import { league } from "../core/index.ts";
import { helpers } from "../util/index.ts";
import { getDefaultSettings } from "../views/newLeague.ts";
import { getClient, getLobbyState, getSession, setLocalLid } from "./index.ts";
import { setImportingSnapshot, withLeagueDbLock } from "./importLock.ts";

// This creates an import cycle (api/index.ts -> api/multiplayer.ts -> ... ->
// here), which is safe because the api object is only dereferenced at call
// time, long after all modules have finished evaluating.
import api from "../api/index.ts";

export const getMyClaimedTid = async (leagueId: string) => {
	const session = await getSession();
	if (!session) {
		throw new Error("Not signed in");
	}
	const state = await getLobbyState(leagueId);
	const me = state.members.find((m) => m.user_id === session.userId);
	if (!me) {
		throw new Error("You are not a member of this league");
	}
	if (me.tid === null) {
		throw new Error("You have not claimed a team yet");
	}
	return { ...state, myTid: me.tid, myUserId: session.userId };
};

const humanTids = (members: { tid: number | null }[]) =>
	members
		.map((m) => m.tid)
		.filter((tid): tid is number => tid !== null)
		.sort((a, b) => a - b);

// Every human team belongs to userTids so the AI never manages a friend's
// roster (signings, trades, roster sorting all check this). Spectator is
// forced off: it's a runner-only sim aid and must never survive an import
// onto a human's client.
export const applyMultiTeamMode = async (
	members: { tid: number | null }[],
	myTid: number,
) => {
	await league.setGameAttributes({
		userTids: humanTids(members),
		userTid: myTid,
		spectator: false,
	});
	await league.updateMeta();
};

// Copied from getSettingsFromGameAttributes in ui/views/NewLeague/index.tsx
// (not importable here because it lives in a UI module): seed the settings
// object from the snapshot's game attributes so the import preserves them.
export const settingsFromGameAttributes = (
	gameAttributes: Record<string, unknown> | undefined,
) => {
	const newSettings = helpers.deepCopy(getDefaultSettings());

	if (gameAttributes) {
		for (const key of helpers.keys(newSettings)) {
			if (
				key === "noStartingInjuries" ||
				key === "randomization" ||
				key === "realStats" ||
				key === "giveMeWorstRoster"
			) {
				continue;
			}

			const value = unwrapGameAttribute(gameAttributes, key);
			if (value !== undefined) {
				if (key === "repeatSeason") {
					newSettings[key] = (value as any)?.type;
				} else {
					(newSettings[key] as any) = value;
				}
			}
		}
	}

	return newSettings;
};

// Download a canonical snapshot from storage and import it as a local league.
// With importLid set, it replaces that existing league in place (createStream
// deletes the old database first); otherwise a fresh league is created.
export const importSnapshotToLeague = async ({
	leagueId,
	snapshotPath,
	importLid,
	conditions,
}: {
	leagueId: string;
	snapshotPath: string;
	importLid?: number;
	conditions: Conditions;
}): Promise<{ lid: number }> => {
	// See importLock.ts: the DB lock makes this mutually exclusive with
	// beforeLeague (league page loads), and the importing flag suppresses
	// realtime-driven view refreshes while the DB is deleted/recreated here.
	return withLeagueDbLock(
		async () => {
			setImportingSnapshot(true);
			// Diagnostic breadcrumb: a full snapshot import means the entire league
			// database was torn down and rebuilt. Counting them is how we verify
			// the draft actually stopped doing that once per pick (v17).
			(globalThis as any).mpImportCount =
				((globalThis as any).mpImportCount ?? 0) + 1;
			try {
				return await importSnapshotToLeagueInner({
					leagueId,
					snapshotPath,
					importLid,
					conditions,
				});
			} finally {
				setImportingSnapshot(false);
			}
		},
		`import(${snapshotPath.slice(-40)})`,
	);
};

const importSnapshotToLeagueInner = async ({
	leagueId,
	snapshotPath,
	importLid,
	conditions,
}: {
	leagueId: string;
	snapshotPath: string;
	importLid?: number;
	conditions: Conditions;
}): Promise<{ lid: number }> => {
	const { league: mpLeague, members, myTid } = await getMyClaimedTid(leagueId);

	const signed = await getClient()
		.storage.from("snapshots")
		.createSignedUrl(snapshotPath, 3600);
	if (signed.error) {
		throw new Error(`Snapshot download failed: ${signed.error.message}`);
	}
	const url = signed.data.signedUrl;

	// leagueCreationID must be set, otherwise initialCheck does not populate
	// basicInfo.keys and the import below would keep nothing from the snapshot
	// (silently generating a random league instead).
	const { basicInfo } = await api.leagueFileUpload.initialCheck(
		{
			file: url,
			leagueCreationID: `mp-check-${leagueId}`,
		},
		conditions,
	);
	if (!basicInfo.keys.has("players")) {
		throw new Error(
			"Snapshot import failed: no players found in the downloaded league file",
		);
	}

	const lid = await api.main.createLeague(
		{
			name: mpLeague.name,
			tid: myTid,
			file: undefined,
			url,
			shuffleRosters: false,
			importLid,
			getLeagueOptions: undefined,
			keptKeys: [...basicInfo.keys],
			confs:
				(unwrapGameAttribute(basicInfo.gameAttributes ?? {}, "confs") as any) ??
				DEFAULT_CONFS,
			divs:
				(unwrapGameAttribute(basicInfo.gameAttributes ?? {}, "divs") as any) ??
				DEFAULT_DIVS,
			teamsFromInput: (basicInfo.teams ?? helpers.getTeamsDefault()) as any,
			settings: settingsFromGameAttributes(basicInfo.gameAttributes),
			fromFile: {
				gameAttributes: basicInfo.gameAttributes,
				hasRookieContracts: basicInfo.hasRookieContracts,
				maxGid: basicInfo.maxGid,
				startingSeason: basicInfo.startingSeason,
				teams: basicInfo.teams,
				version: basicInfo.version,
			},
			startingSeasonFromInput: undefined,
			leagueCreationID: `mp-${leagueId}`,
		},
		conditions,
	);

	await applyMultiTeamMode(members, myTid);
	await setLocalLid(leagueId, lid);

	return { lid };
};
